import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Mail, Phone, MapPin, ArrowRight, Loader2, MessageSquare } from "lucide-react";
import { trackFormSubmit } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  company: z.string().optional(),
  subject: z.string().min(5, "Subject is required"),
  message: z.string().min(10, "Please provide more details"),
});

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      trackFormSubmit("contact", "/contact");
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="noise-overlay">
      <Helmet>
        <title>Contact Prithira Global | Eco-Friendly Packaging Supplier India</title>
        <meta name="description" content="Get in touch with Prithira Global for biodegradable packaging quotes, bulk orders, and export inquiries. Email, phone, and form-based contact." />
        <meta name="keywords" content="contact prithira global, eco packaging supplier contact, biodegradable exporter phone, sustainable packaging quote India" />
        <link rel="canonical" href="https://www.prithiraglobal.com/contact" />
        <meta property="og:title" content="Contact Prithira Global" />
        <meta property="og:description" content="Reach Prithira Global for biodegradable packaging quotes, bulk orders, and export inquiries." />
        <meta property="og:url" content="https://www.prithiraglobal.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Get In Touch</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Contact <span className="italic">Us</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              Have questions about our products or interested in discussing a
              partnership? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="heading-md text-stone-900 mb-8">
                Reach <span className="italic">Out</span>
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Email Us</h3>
                    <p className="text-stone-600 text-sm font-light">
                      info@prithiraglobal.com
                    </p>
                    <p className="text-stone-500 text-xs mt-1">
                      We respond within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Call Us</h3>
                    <p className="text-stone-600 text-sm font-light">
                      +91 9830195737
                    </p>
                    <p className="text-stone-600 text-sm font-light">
                      +91 9674010032
                    </p>
                    <p className="text-stone-500 text-xs mt-1">
                      Mon-Sat, 9:00 AM - 6:00 PM IST
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Location</h3>
                    <p className="text-stone-600 text-sm font-light">
                      100/12, Jessore Road, Bhagabati Park,
                      <br />
                      Dumdum, Kolkata 700074,
                      <br />
                      West Bengal, India
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-12 p-6 bg-sand-200/50 rounded-xl">
                <h3 className="font-heading text-lg text-stone-900 mb-4">
                  Quick Links
                </h3>
                <ul className="space-y-2 text-sm text-stone-600">
                  <li>
                    <a href="/partner" className="hover:text-primary transition-colors">
                      Partnership Inquiry →
                    </a>
                  </li>
                  <li>
                    <a href="/products" className="hover:text-primary transition-colors">
                      View Products →
                    </a>
                  </li>
                  <li>
                    <a href="/export" className="hover:text-primary transition-colors">
                      Export Information →
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-sand-200/30 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <MessageSquare className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="heading-md text-stone-900 mb-4">
                    Message <span className="italic">Received</span>
                  </h3>
                  <p className="text-stone-600 font-light mb-8">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                  <Button
                    data-testid="send-another-btn"
                    onClick={() => {
                      setSubmitted(false);
                      form.reset();
                    }}
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base font-medium border-2 border-primary text-primary hover:bg-primary/5"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <div className="bg-sand-200/30 rounded-2xl p-8 md:p-12">
                  <h3 className="heading-md text-stone-900 mb-8">
                    Send a <span className="italic">Message</span>
                  </h3>

                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-6"
                      data-testid="contact-form"
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Name *</FormLabel>
                              <FormControl>
                                <Input
                                  data-testid="contact-name-input"
                                  placeholder="John Doe"
                                  className="h-12 rounded-lg bg-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address *</FormLabel>
                              <FormControl>
                                <Input
                                  data-testid="contact-email-input"
                                  type="email"
                                  placeholder="email@example.com"
                                  className="h-12 rounded-lg bg-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                data-testid="contact-company-input"
                                placeholder="Your company name"
                                className="h-12 rounded-lg bg-white"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subject *</FormLabel>
                            <FormControl>
                              <Input
                                data-testid="contact-subject-input"
                                placeholder="What is this regarding?"
                                className="h-12 rounded-lg bg-white"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message *</FormLabel>
                            <FormControl>
                              <Textarea
                                data-testid="contact-message-textarea"
                                placeholder="Your message..."
                                className="min-h-[150px] rounded-lg bg-white resize-none"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        data-testid="contact-submit-btn"
                        disabled={isSubmitting}
                        className="w-full rounded-full py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 btn-lift"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
